import { Component, effect, inject } from '@angular/core';
import { SelectOption } from '@navstation-portal/shared/data-model/common';
import { FormControlStatus, FormsModule } from '@angular/forms';
import { injectDispatch } from '@ngrx/signals/events';
import { FuelSpecsStore } from '@navstation-portal/fuel-specs/data-access/store';
import {
  offshoreConsumptionsFormEvents,
  offshoreConsumptionsEvents,
  OffshoreConsumptionsFormStore,
  AbstractStoreConnectedSectionComponent,
} from '@navstation-portal/reporting/data-access/offshore-report';
import { FuelSpecsTableComponent } from '../shared/fuel-specs-table/fuel-specs-table.component';

@Component({
  selector: 're-fuel-specs-non-bdn',
  templateUrl: './fuel-specs-non-bdn.component.html',
  imports: [FuelSpecsTableComponent, FormsModule],
})
export class FuelSpecsNonBdnComponent extends AbstractStoreConnectedSectionComponent {
  tabId = 'consumptions';
  sectionId = 'fuelSpecs';
  events = injectDispatch(offshoreConsumptionsFormEvents);
  extraEvents = injectDispatch(offshoreConsumptionsEvents);
  consumptionsStore = inject(OffshoreConsumptionsFormStore);
  fuelSpecsStore = inject(FuelSpecsStore);
  private autoAppliedIsoByRowId = new Map<string, string>();

  readonly rows: { key: string; label: string; required?: boolean }[] = [
    { key: 'fuelType', label: 'Fuel Type' },
    { key: 'isoFuelGrade', label: 'ISO Fuel Grade', required: true },
    { key: 'lcv', label: 'LCV (kJ/kg)', required: true },
    { key: 'sulphur', label: 'Sulphur (%)', required: true },
    { key: 'density', label: 'Density at 15°C (kg/m3)', required: true },
    { key: 'fuelTemp', label: 'Fuel Temp at FM (°C)', required: true },
    { key: 'viscosity', label: 'Viscosity (cSt)' },
  ] as const;

  private autoApplySingleIsoFromVm = effect(() => {
    const fuelSpecsTableVm = this.consumptionsStore.fuelSpecsTableVm();
    const entries = fuelSpecsTableVm.entries;
    const autoApplyByRow = fuelSpecsTableVm.autoApplyIsoGradeByRow;
    const seenRowIds = new Set<string>();

    entries.forEach((entry, index) => {
      const rowId = String(entry?.rowId?.value ?? index);
      seenRowIds.add(rowId);

      const fuelType = entry?.fuelType?.value;
      const selectedIsoGrade = entry?.isoFuelGrade?.value;
      const autoIsoGrade = autoApplyByRow[index];

      if (!fuelType || !autoIsoGrade) {
        this.autoAppliedIsoByRowId.delete(rowId);
        return;
      }

      if (selectedIsoGrade) {
        this.autoAppliedIsoByRowId.delete(rowId);
        return;
      }

      const signature = `${String(fuelType.code)}|${String(autoIsoGrade.code)}`;
      if (this.autoAppliedIsoByRowId.get(rowId) === signature) {
        return;
      }

      this.autoAppliedIsoByRowId.set(rowId, signature);
      this.onFuelISOGradeChange({
        index,
        fuelType,
        isoGrade: autoIsoGrade,
      });
    });

    Array.from(this.autoAppliedIsoByRowId.keys()).forEach((rowId) => {
      if (!seenRowIds.has(rowId)) {
        this.autoAppliedIsoByRowId.delete(rowId);
      }
    });
  });

  get fuelTypeOptionsByRow(): SelectOption[][] {
    return (
      this.consumptionsStore.vm() as {
        fuelTypeOptionsByRow: SelectOption[][];
      }
    ).fuelTypeOptionsByRow;
  }

  removeEntry(index: number | undefined) {
    this.extraEvents.removeFuelSpec(index);
  }

  addEntry() {
    this.extraEvents.addFuelSpec();
  }

  fuelTypeChanged = (index: number) => {
    this.extraEvents.clearFuelSpec(index);
  };

  onFuelISOGradeChange(payload: {
    index: number;
    fuelType?: SelectOption;
    isoGrade: SelectOption;
  }) {
    const { index, fuelType, isoGrade } = payload;

    const lookedUpValues =
      this.fuelSpecsStore.getFuelSpecValues()(fuelType, isoGrade) ?? {};

    this.extraEvents.updateFuelSpec({
      index: index,
      isoGrade,
      ...lookedUpValues,
    });
  }

  onRangeStatusChanged(payload: {
    index: number;
    field: string;
    status: 'VALID' | 'INVALID';
  }) {
    this.events.updateFormStateStatus({
      sectionId: this.sectionId,
      fieldId: 'entries',
      index: payload.index,
      property: payload.field,
      value: payload.status as FormControlStatus,
    });
  }
}
